import { html, nothing } from 'lit';
import componentStyles from './mlp-hero.css.lit.ts';
import { OutlineElement } from '../../base/outline-element/outline-element.ts';
import { SlottedController } from '../../controllers/slotted-controller.ts';
import { unsafeHTML } from 'lit/directives/unsafe-html';
import hfMark from '../icon/assets/icons/hf-mark.svg';

/**
 * Marketing Landing Page Hero.
 *
 * @element hf-mlp-hero
 * @slot hero-text - copy for the hero
 * @slot media - image or video for the hero
 */
export default class MlpHero extends OutlineElement {
  static styles = [super.styles, componentStyles];
  static get properties() {
    return {
      /**
       * Main content area title
       */
      title: { type: String },
      /**
       * Main content area eyebrow above title
       */
      eyebrow: { type: String },
      /**
       * Main content area text below title
       */
      text: { type: String },
      /**
       * Button text
       */
      ctaText: { type: String },
      /**
       * Button url
       */
      ctaUrl: { type: String },
      /**
       * Button target
       */
      ctaTarget: { type: String },
      /**
       * Youtube video ID
       */
      videoId: { type: String },
      /**
       * Alternative Image URL
       */
      alternativeImage: { type: String },
    };
  }

  private = new SlottedController(this);

  getVideoMarkup() {
    if (this.alternativeImage) {
      return html`
        <hf-lightbox type="iframe">
          <div slot="preview">
            <span class="lightbox__play"></span>
            <img src="${this.alternativeImage}" />
          </div>

          <div slot="content">
            <iframe
              width="560"
              height="315"
              class="lightbox__iframe"
              allowfullscreen="true"
              src="https://www.youtube.com/embed/${this
                .videoId}?autoplay=1&mute=1"
            >
            </iframe>
          </div>
        </hf-lightbox>
      `;
    } else {
      return html`
        <hf-lightbox type="iframe">
          <div slot="preview">
            <span class="lightbox__play"></span>
            <img src="https://img.youtube.com/vi/${this.videoId}/default.jpg" class="lightbox__preview" />
          </div>

          <div slot="content">
            <iframe
              width="560"
              height="315"
              class="lightbox__iframe"
              allowfullscreen="true"
              src="https://www.youtube.com/embed/${this
                .videoId}?autoplay=1&mute=1"
            >
            </iframe>
          </div>
        </hf-lightbox>
      `;
    }
  }

  render() {
    return html`
      <div class="mlp-hero__wrapper">
        <div class="mlp-hero__content_background"></div>
        <div class="mlp-hero__content_wrapper">
          <div
            class="${this.eyebrow
              ? 'mlp-hero__content'
              : 'mlp-hero__content mlp-hero__content--no-eyebrow'}"
          >
            ${this.eyebrow
              ? html`<hf-heading
                  level=""
                  levelStyle="eyebrow"
                  class="mlp-hero__eyebrow mlp-hero__eyebrow--eyebrow"
                >
                  ${this.eyebrow}
                </hf-heading>`
              : nothing}

            <hf-heading
              level=""
              levelstyle="mlp-hero"
              class="${this.eyebrow
                ? 'mlp-hero__title'
                : 'mlp-hero__title mlp-hero__title--no-eyebrow'}"
            >
              ${this.title}
            </hf-heading>
            <hf-paragraph
              styleclass="mlp-hero"
              class="mlp-hero__text">
              <slot name="hero-text"></slot>
            </hf-paragraph>
            ${(this.ctaUrl &&
              this.ctaText &&
              html`
                <div class="mlp-hero__button">
                  <hf-link-button
                    type="primary"
                    target="${this.ctaTarget}"
                    href="${this.ctaUrl}"
                    linkClass="btn__link--hero-cta-links"
                  >
                    ${this.ctaText}
                  </hf-link-button>
                </div>
              `) ||
            ''}
          </div>
          <span class="mlp-hero__icon">${unsafeHTML(hfMark)}</span>
        </div>
        <div class="mlp-hero__media">
          ${this.videoId
            ? html`${this.getVideoMarkup()}`
            : html`<slot name="media"></slot>`}
        </div>
      </div>
    `;
  }
}

customElements.define('hf-mlp-hero', MlpHero);
